import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FmGridProps } from "domains/file-manager/components/FileManagerV2/FmGrid";
import { useAvoidFlicker } from "domains/file-manager/hooks/useAvoidFlicker";
import { FmFileModel } from "domains/file-manager/interfacesV2";
import { useHover } from "domains/ui/hooks/useHover";
import { useWindowSize } from "domains/ui/hooks/useWindowSize";
import { noop } from "lodash";
import _ from "lodash";
import {
  useContainerPosition,
  useInfiniteLoader,
  useMasonry,
  usePositioner,
  useResizeObserver,
} from "masonic";
import { useScroller } from "mini-virtual-list";

import { Box, BoxProps, HStack, Skeleton } from "@chakra-ui/react";

import Card from "./Card";

export type GridProps = FmGridProps<"model">;

export default function Grid({
  files,

  columnsCount,

  isSkeleton = false,

  isSelectable = false,
  isSelectionForced = false,
  selection,
  isSelectionMaxReached,
  onSelect,

  onCardClick,
  withLink,
  onEndReached,
  onRender,

  scrollRef,
  containerRef,
  positionerDeps = [],
}: GridProps) {
  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const { offset, width: itemWidth } = useContainerPosition(containerRef, [
    windowWidth,
    windowHeight,
    ...positionerDeps,
  ]);
  const { scrollTop } = useScroller(scrollRef ?? window, {
    offset,
  });
  const [initialCardWidth, setInitialCardWidth] = useState<
    number | undefined
  >();
  const [maxCardWidth, setMaxCardWidth] = useState<number | undefined>();

  const [fileLength, setFileLength] = useState(files.length);
  /** Used to reset positions of already existing files, when we delete files, but not when new files are loaded */
  const willChangeOnlyIfLessFilesThanBefore = useMemo(() => {
    if (files.length < fileLength) {
      return {};
    }
    setFileLength(files.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files.length]);

  const positioner = usePositioner(
    {
      width: itemWidth,
      columnGutter: 8,
      columnCount: columnsCount,
      rowGutter: 8,
    },
    [columnsCount, willChangeOnlyIfLessFilesThanBefore, itemWidth]
  );
  const resizeObserver = useResizeObserver(positioner);

  const MasonryCardWithProps = useCallback(
    (props: Omit<MasonryCardProps, keyof MasonryCardCommonProps>) => (
      <MasonryCard
        onClick={onCardClick}
        withLink={withLink}
        containerRef={containerRef}
        {...props}
      />
    ),
    [containerRef, onCardClick, withLink]
  );

  const masonryItems = useMemo(
    () =>
      files.map((file): MasonryCardProps["data"] => {
        return {
          file,
          isDisabled: !!isSelectionMaxReached && !selection.includes(file.id),
          cardWidth: positioner.columnWidth,
          isSelected: selection.includes(file.id),
          isShiftSelectable: isSelectionForced || !!selection.length,
          isSelectable,
          onSelect,
        };
      }),
    [
      files,
      positioner.columnWidth,
      selection,
      isSelectionForced,
      isSelectionMaxReached,
      isSelectable,
      onSelect,
    ]
  );

  // ----------------------------------

  const handleEndReachCheck = useInfiniteLoader(async () => onEndReached?.(), {
    isItemLoaded: (index, items) => !!items[index],
  });

  const handleMasonryRender = useCallback(
    (startIndex: number, stopIndex: number, items: typeof masonryItems) => {
      onRender?.();
      handleEndReachCheck(startIndex, stopIndex, items);
    },
    [onRender, handleEndReachCheck]
  );

  // ----------------------------------

  useEffect(() => {
    if (positioner.columnWidth < 40) return;

    if (!maxCardWidth || positioner.columnWidth > maxCardWidth) {
      setMaxCardWidth(positioner.columnWidth);
    }

    if (!initialCardWidth) {
      setInitialCardWidth(positioner.columnWidth);
    }
  }, [positioner.columnWidth, initialCardWidth, maxCardWidth]);

  // ----------------------------------

  const masonry = useAvoidFlicker(
    useMasonry({
      scrollTop,
      positioner,
      resizeObserver,
      items: masonryItems,
      onRender: handleMasonryRender,
      className: "masonic",
      itemKey: (data) => data.file.id,
      overscanBy: 2,
      height: windowHeight || 0,
      render: MasonryCardWithProps,
    })
  );

  if (isSkeleton) {
    return (
      <HStack spacing={2}>
        {_.range(columnsCount).map((idx) => (
          <Box key={`${idx}`} flex={1} aspectRatio="1/1">
            <Skeleton w="100%" h="100%" />
          </Box>
        ))}
      </HStack>
    );
  }

  return <Box sx={{ ".masonic": { outline: "none" } }}>{masonry}</Box>;
}

type MasonryCardCommonProps = {
  onClick?: (file: FmFileModel, index: number) => void;
  withLink?: boolean;
  containerRef: RefObject<HTMLDivElement>;
};

type MasonryCardProps = MasonryCardCommonProps & {
  width: number;
  index: number;
  data: {
    file: FmFileModel;
    isDisabled: boolean;
    cardWidth: number;
    isSelected: boolean;
    isShiftSelectable: boolean;
    isSelectable?: boolean;
    isPinned?: boolean;
    onSelect: FmGridProps["onSelect"];
  };
};

const MasonryCardWithoutMemo = function MasonryCard({
  onClick,
  withLink,
  data: {
    file,
    isDisabled,
    isSelected = false,
    isShiftSelectable = false,
    isSelectable = false,
    onSelect,
    cardWidth,
  },
  index,
}: MasonryCardProps) {
  const [hoverRef, isHovered] = useHover<HTMLDivElement>();

  const thumbnailUrl = file.thumbnail;

  const imgProps: BoxProps = useMemo(() => {
    return {
      position: "absolute",
      top: 0,
      objectFit: "cover",
    };
  }, []);
  const containerProps: BoxProps = useMemo(() => {
    return {
      pt: "100%",
    };
  }, []);

  const handleClick = useCallback(
    (file: FmFileModel) => onClick?.(file, index),
    [onClick, index]
  );

  return (
    <Box
      ref={hoverRef}
      pos="relative"
      h={`${cardWidth + 40}px`}
      minH="30px"
      cursor={isDisabled ? "default" : "pointer"}
      data-id={file.id}
      data-key={index}
      data-testid="model-gallery-grid-cell"
    >
      <Card
        file={file}
        thumbnail={thumbnailUrl}
        isDisabled={isDisabled}
        isSelectable={isSelectable && !isDisabled}
        isShiftSelectable={isShiftSelectable}
        isSelected={isSelected}
        isHovered={isHovered}
        onSelect={isDisabled ? noop : onSelect}
        onClick={handleClick}
        withLink={withLink}
        containerProps={containerProps}
        cardWidth={cardWidth}
        imgProps={{
          ...imgProps,
          alt: file.meta.name,
        }}
      />
    </Box>
  );
};

const MasonryCard = React.memo(
  MasonryCardWithoutMemo
) as typeof MasonryCardWithoutMemo;
